<template>
  <!-- Mobile/Tablet Overlay -->
  <div
    v-if="showMobileOverlay"
    class="mobile-overlay"
    @click="closeMobileSidebar"
  ></div>

  <div
    class="lesson-sidebar"
    :class="{
      collapsed: collapsed,
      'mobile-slide-in': showMobileOverlay,
      'mobile-hidden': !showMobileOverlay,
    }"
  >
    <!-- Desktop toggle button (hidden on mobile) -->
    <div class="sidebar-header desktop-only">
      <button
        class="sidebar-toggle"
        @click="toggleSidebar"
        :title="collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
        :aria-label="collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
        :aria-expanded="!collapsed"
      >
        <ChevronLeftIcon
          class="toggle-icon"
          :style="{
            transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
          }"
        />
      </button>
    </div>

    <!-- Mobile close button -->
    <button
      class="mobile-close-btn mobile-only"
      @click="closeMobileSidebar"
      aria-label="Close Sidebar"
    >
      <img src="/images/close.svg" alt="Close" />
    </button>

    <div class="sidebar-content" v-if="!collapsed">
      <div class="sidebar-title">
        <div class="content-info-heading">
          <span>Lesson Details</span>
        </div>
      </div>

      <!-- Progress Section -->
      <div class="progress-section" v-if="isAuthenticated">
        <div class="progress-header">
          <span class="progress-title">Progress</span>
          <span class="progress-percentage"
            >{{ getProgressPercentage() }}%</span
          >
        </div>
        <div class="progress-bar-container">
          <div
            class="progress-bar"
            :style="`width: ${getProgressPercentage()}%`"
          ></div>
        </div>
      </div>

      <div class="category-list">
        <div v-if="filteredCategories.length > 0">
          <div
            v-for="(category, index) in filteredCategories"
            :key="index"
            class="category-item"
          >
            <div class="category-title">{{ category.title }}</div>
            <div class="category-terms">
              <template v-if="category.terms && category.terms.length > 0">
                <template
                  v-for="(term, termIndex) in category.terms"
                  :key="termIndex"
                >
                  <template v-if="term.description">
                    <v-tooltip
                      :text="term.description"
                      location="top"
                      max-width="300"
                      content-class="custom-tooltip"
                    >
                      <template v-slot:activator="{ props }">
                        <span class="term-tag" v-bind="props">
                          {{ term.title }}
                        </span>
                      </template>
                    </v-tooltip>
                  </template>
                  <template v-else>
                    <span class="term-tag">{{ term.title }}</span>
                  </template>
                </template>
              </template>
              <div v-else class="empty-terms-state">
                <div class="empty-terms-icon">
                  <v-icon size="24" color="#cbd5e1">mdi-tag-outline</v-icon>
                </div>
                <p class="empty-terms-text">No tags available</p>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-categories-state">
          <div class="empty-categories-icon">
            <v-icon size="56" color="#cbd5e1">mdi-bookmark-outline</v-icon>
          </div>
          <div class="empty-categories-text">
            <h3>No Details Available</h3>
            <p>
              Category information will appear here when it's added to this
              lesson.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { CATEGORY_TYPES } from "@/utils/constants";
import {
  computed,
  defineProps,
  defineEmits,
  ref,
  watch,
  onMounted,
  onUnmounted,
} from "vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";

const props = defineProps({
  categories: {
    type: Array,
    required: true,
    default: () => [],
  },
  collapsed: {
    type: Boolean,
    default: false,
  },
  showMobile: {
    type: Boolean,
    default: false,
  },
  isAuthenticated: {
    type: Boolean,
    default: false,
  },
  progress: {
    type: [Number, Object],
    default: 0,
  },
});

const emit = defineEmits(["update:collapsed", "update:showMobile"]);

const showMobileOverlay = ref(false);

// Filter out software category for sidebar display
const filteredCategories = computed(() => {
  return props.categories.filter((category) => {
    const isLessonCategory = category.category_for.includes(
      CATEGORY_TYPES.LessonPlan
    );
    const isSoftwareCategory = category.name === "software";
    const isSchoolCategory = category.name === "school";
    return isLessonCategory && !isSoftwareCategory && !isSchoolCategory;
  });
});

const toggleSidebar = () => {
  emit("update:collapsed", !props.collapsed);
};

const closeMobileSidebar = () => {
  showMobileOverlay.value = false;
  emit("update:showMobile", false);
};

// Watch for mobile show prop changes
watch(
  () => props.showMobile,
  (newValue) => {
    showMobileOverlay.value = newValue;
    if (newValue) {
      document.body.classList.add("sidebar-open");
    } else {
      document.body.classList.remove("sidebar-open");
    }
  }
);

// Handle escape key to close mobile sidebar
const handleEscKey = (event) => {
  if (event.key === "Escape" && showMobileOverlay.value) {
    closeMobileSidebar();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleEscKey);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscKey);
  document.body.classList.remove("sidebar-open");
});

// Calculate progress percentage from props
const getProgressPercentage = () => {
  if (!props.progress) return 0;

  // If progress is a number, treat it as a percentage
  if (typeof props.progress === "number") {
    return Math.min(Math.max(props.progress, 0), 100);
  }

  // If progress is an object, calculate based on completed/total
  if (
    typeof props.progress === "object" &&
    props.progress.completed !== undefined &&
    props.progress.total !== undefined
  ) {
    if (props.progress.total === 0) return 0;
    return Math.round((props.progress.completed / props.progress.total) * 100);
  }

  return 0;
};
</script>

<style scoped>
/* Desktop Styles */
.lesson-sidebar {
  width: 300px;
  min-width: 300px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  height: fit-content;
  position: sticky;
  top: 20px;
  transition: all 0.3s ease;
  margin-right: 24px;
}

.lesson-sidebar.collapsed {
  width: 60px;
  min-width: 60px;
}

.sidebar-header {
  padding: 15px;
  display: flex;
  justify-content: flex-end;
  border-radius: 12px 12px 0 0;
}

.sidebar-content {
  padding: 0;
}

.content-info-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 15px 20px;
  color: var(--primary-blue);
  font-weight: 500;
  font-size: var(--fs-big);
}

.content-info-heading svg {
  stroke: #009688;
}

/* Progress Section */
.progress-section {
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 10px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-title {
  font-weight: 600;
  color: #374151;
  font-size: 1rem;
}

.progress-percentage {
  font-weight: 700;
  color: #e00069;
  font-size: 1.1rem;
}

.progress-bar-container {
  width: 100%;
  height: 10px;
  background-color: #f3f4f6;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.progress-bar {
  height: 100%;
  background: #e00069;
  border-radius: 6px;
  transition: width 0.8s ease-in-out;
  position: relative;
  overflow: hidden;
}

.progress-bar::after {
  content: "";
  position: absolute;
  top: 0;
  left: -50%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer 2.5s ease-in-out infinite;
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.category-list {
  padding: 0;
}

.category-item {
  padding: 0;
}

.category-title {
  font-weight: 700;
  color: var(--black-body-text);
  padding: 15px 20px 8px;
  font-size: var(--fs-big);
  text-transform: capitalize;
}

.category-terms {
  padding: 0 20px 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.term-tag {
  background-color: var(--white-hover-1);
  border: 1px solid var(--white-hover-1);
  border-radius: 6px;
  font-size: var(--text-small);
  padding: 6px 12px;
  color: var(--black-body-text);
  display: inline-block;
  transition: all 0.2s ease;
  cursor: default;
}

.term-tag:hover {
  background-color: #e5e7eb;
  border-color: #d1d5db;
}

/* Empty States */
.empty-categories-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  border-radius: 12px;
  margin: 20px;
  min-height: 200px;
  transition: all 0.3s ease;
}

.empty-categories-icon {
  margin-bottom: 16px;
  opacity: 0.6;
  transition: all 0.3s ease;
}

.empty-categories-state:hover .empty-categories-icon {
  opacity: 0.8;
  transform: translateY(-2px);
}

.empty-categories-text h3 {
  color: #64748b;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  letter-spacing: -0.025em;
}

.empty-categories-text p {
  color: #94a3b8;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
  max-width: 280px;
}

.empty-terms-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 15px;
  text-align: center;
  background-color: #f8fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 8px;
  margin: 0 20px;
  transition: all 0.3s ease;
}

.empty-terms-state:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
}

.empty-terms-icon {
  margin-bottom: 8px;
  opacity: 0.6;
  transition: all 0.3s ease;
}

.empty-terms-state:hover .empty-terms-icon {
  opacity: 0.8;
  transform: translateY(-1px);
}

.empty-terms-text {
  color: #94a3b8;
  font-size: 0.8rem;
  margin: 0;
  font-style: italic;
}

.sidebar-title {
  margin-bottom: 0;
}

.sidebar-toggle {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s ease;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-toggle:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.toggle-icon {
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
}

/* Mobile overlay */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
  opacity: 0;
  animation: fadeIn 0.3s ease forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

.mobile-close-btn {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 1000;
  background-color: transparent;
  border: none;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-close-btn:hover {
  transform: scale(1.05);
}

.mobile-close-btn img {
  width: 20px;
  height: 20px;
}

/* Responsive visibility classes */
.desktop-only {
  display: flex;
}

.mobile-only {
  display: none;
}

/* Global body class when sidebar is open on mobile */
:global(.sidebar-open) {
  overflow: hidden;
}

/* Mobile/Tablet Styles */
@media (max-width: 1024px) {
  .lesson-sidebar {
    position: fixed;
    top: auto;
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    max-width: 100%;
    height: auto;
    max-height: 85vh;
    z-index: 999;
    padding-top: 0;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    box-shadow: 0px -4px 15px rgba(0, 0, 0, 0.15);
    border-left: none;
    border-radius: 28px 28px 0 0;
    border-top: 1px solid #d0d5dd;
  }

  .lesson-sidebar.mobile-hidden {
    transform: translateY(100%);
  }

  .lesson-sidebar.mobile-slide-in {
    transform: translateY(0);
  }

  .sidebar-content {
    height: auto;
    max-height: calc(85vh - 20px);
    overflow-y: auto;
    padding: 0 0 20px 0;
  }

  .content-info-heading {
    padding: 20px 20px 15px;
    border-bottom: 1px solid #d0d5dd;
    margin-bottom: 15px;
    color: var(--primary-blue);
    font-size: var(--fs-title-large, 22px);
    font-weight: var(--font-weight-medium);
  }

  .category-item {
    margin-bottom: 5px;
  }

  .category-title {
    padding: 10px 20px 8px;
    color: var(--black-body-text);
    font-size: var(--fs-title-big);
    font-weight: var(--font-weight-bold);
  }

  .category-terms {
    padding: 0 20px 15px;
    gap: 10px;
  }

  .term-tag {
    font-size: 0.85rem;
    padding: 8px 14px;
    border-radius: 20px;
    font-weight: 500;
  }

  /* Hide desktop elements on mobile */
  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: flex;
  }

  /* Empty states mobile adjustments */
  .empty-categories-state {
    padding: 30px 15px;
    margin: 15px;
    min-height: 160px;
  }

  .empty-categories-text h3 {
    font-size: 1rem;
  }

  .empty-categories-text p {
    font-size: 0.8rem;
    max-width: 240px;
  }

  .empty-terms-state {
    padding: 15px 10px;
    margin: 0 15px;
  }

  .empty-terms-text {
    font-size: 0.75rem;
  }
}

@media (max-width: 768px) {
  .lesson-sidebar {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .lesson-sidebar {
    width: 100%;
  }

  .content-info-heading {
    padding: 15px 15px 10px;
    font-size: var(--fs-large);
  }

  .category-title {
    font-size: var(--fs-big);
    padding: 8px 15px 6px;
  }

  .category-terms {
    padding: 0 15px 12px;
    gap: 8px;
  }

  .term-tag {
    font-size: var(--fs-small);
    padding: 6px 12px;
  }

  .mobile-close-btn {
    top: 15px;
    right: 15px;
    width: 24px;
    height: 24px;
  }

  .mobile-close-btn img {
    width: 20px;
    height: 20px;
  }

  /* Empty states small mobile adjustments */
  .empty-categories-state {
    padding: 25px 10px;
    margin: 10px;
    min-height: 140px;
  }

  .empty-categories-text h3 {
    font-size: 0.95rem;
  }

  .empty-categories-text p {
    font-size: 0.75rem;
    max-width: 200px;
  }

  .empty-terms-state {
    padding: 12px 8px;
    margin: 0 10px;
  }

  .empty-terms-text {
    font-size: 0.7rem;
  }
}
</style>

<style></style>
